/* global Word, console */

/**
 * Character Styles Applicator
 *
 * Translates declarative rule actions into Word.js operations. Runs
 * after paragraph styles are applied so tracked changes capture both
 * formatting removals and character style inserts on the styled document.
 *
 * Pipeline per paragraph:
 *   1. Extract word-level formatting spans via getTextRanges()
 *   2. Build ParagraphFormattingInfo with computed aggregates
 *   3. Pass context to rules engine → receive CharacterStyleAction[]
 *   4. Execute actions in order: removals → span styles → entity styles → paragraph styles
 *
 * @see rules-engine.ts — emits the actions consumed here
 * @see types.ts — FormattingSpan, ParagraphFormattingInfo, CharacterStyleAction
 */

import { buildStyleDefinitionMap, loadStyleProfile, StepProgress } from "src/services";
import { evaluateAllRules } from "./rules-engine";
import { generateUUID } from "src/services";

import type {
  ApplyProgressReporter,
  CharacterStyleAction,
  FormattingSpan,
  FormattingSnapshot,
  ItalicRun,
  ParagraphFormattingInfo,
  PerfSession,
  RuleContext,
} from "src/services";
import type { CharacterStyleProfileEntry, ClassificationResponse, EntityHintType } from "src/types";

// ── Result type ──────────────────────────────────────────────────────

export interface CharStyleActionRecord {
  paragraphTag: string;
  paragraphId: string;
  paragraphStyle: string;
  actions: CharacterStyleAction[];
  formattingInfo: ParagraphFormattingInfo;
}

export interface CharStyleApplicationResult {
  applied: number;
  skipped: number;
  actionRecords: CharStyleActionRecord[];
  characterStyles: CharacterStyleProfileEntry[];
}

// ── Entity hint confidence thresholds ─────────────────────────────

/**
 * Minimum confidence required for each entity hint type to be applied.
 * Hints below the threshold for their type are filtered out before
 * rule evaluation. Adjust values here to tune entity hint sensitivity.
 */
const ENTITY_HINT_CONFIDENCE_THRESHOLDS: Record<EntityHintType, number> = {
  publication: 1.0,
  foreign_word: 1.0,
  fraction: 0.9,
  speaker: 0.9,
  url: 1.0,
  text_message: 0.9,
};

const CHAR_STYLE_TAG_PREFIX = "mss:c:";
export const CHAR_STYLE_CC_TAG = "character-style";

/**
 * Content-control inserts per pass-2 sync.
 *
 * Batches close on paragraph boundaries, so this is a floor rather than a cap.
 * Sized for timing granularity: a typical run plans a few hundred inserts, so 25
 * yields enough batches to show whether per-insert cost degrades as the pass
 * proceeds, without the fixed per-sync cost dominating each measurement.
 */
const PASS2_BATCH_INSERTS = 25;

/** Queued operations per pass-1 sync. Same sizing rationale as {@link PASS2_BATCH_INSERTS}. */
const PASS1_BATCH_OPERATIONS = 25;

/**
 * Stale content-control deletes per sync.
 *
 * As one sync this took 1m 54s for 788 deletes with no way to show a user it was
 * progressing. Deletes are independent of each other, so a failed batch skips
 * only its own controls. Larger than the pass batches because a delete is
 * cheaper than an insert and there is no timing question left to answer here.
 */
const PHASE0_DELETES_PER_BATCH = 50;

/** getTextRanges delimiters — splits a paragraph into one range per word. */
export const WORD_DELIMITERS = [" ", "\n", "\t", "\u00A0"];

// \u2500\u2500 Partly-italic words \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

/**
 * Characters, beyond Unicode punctuation (\p{P}), at which a partly-italic word
 * may be split. \p{P} already covers dashes, quotes, ? ! . , ; :, brackets and
 * braces, ellipsis, \u00BF \u00A1 \u00AB \u00BB and the like \u2014 add anything else found in
 * manuscripts here (e.g. symbols such as "\u00A7" or "\u2020").
 */
export const SPLIT_BOUNDARY_EXTRA_CHARS: string[] = [];

// Built with the constructor: the `u` flag needs a newer target than es2015.
const UNICODE_PUNCTUATION = new RegExp("\\p{P}", "u");

/**
 * Whether `ch` is a character next to which a partly-italic word may be split.
 *
 * `getTextRanges` splits on whitespace only, so `reinvigorate\u2014the` is a single
 * word range. When only part of it is italic, the italic style must stop at the
 * boundary character instead of covering the whole word.
 */
export function isSplitBoundaryChar(
  ch: string,
  extraChars: readonly string[] = SPLIT_BOUNDARY_EXTRA_CHARS
): boolean {
  return UNICODE_PUNCTUATION.test(ch) || extraChars.includes(ch);
}

function containsSplitBoundaryChar(text: string): boolean {
  return Array.from(text).some((ch) => isSplitBoundaryChar(ch));
}

/** Per-character italic state of a partly-italic word, as read from Word. */
export type WordCharItalics = Array<{ text: string; italic: boolean }>;

/** Character italics keyed by the word's index in `wordRanges.items`. */
export type MixedItalicChars = Map<number, WordCharItalics>;

/**
 * Queue a per-character load for each word whose italic is mixed and that
 * contains a boundary character. Every other word needs nothing extra, so a
 * paragraph without such words costs no additional proxies.
 *
 * Requires `wordRanges` loaded with `items/text` and `items/font/italic`.
 * Read the results with {@link readMixedItalicChars} after the next sync.
 */
export function queueMixedItalicCharLoads(
  wordRanges: Word.RangeCollection
): Map<number, Word.RangeCollection> {
  const pending = new Map<number, Word.RangeCollection>();
  wordRanges.items.forEach((range, index) => {
    const text = range.text ?? "";
    if (range.font.italic !== null || !containsSplitBoundaryChar(text)) return;
    // "?" with wildcards matches any single character: one range per character.
    const chars = range.search("?", { matchWildcards: true });
    chars.load("items/text, items/font/italic");
    pending.set(index, chars);
  });
  return pending;
}

/** Convert loaded per-character ranges into plain data for the snapshot. */
export function readMixedItalicChars(
  pending: Map<number, Word.RangeCollection>
): MixedItalicChars {
  const result: MixedItalicChars = new Map();
  for (const [index, chars] of pending) {
    result.set(
      index,
      chars.items.map((c) => ({ text: c.text ?? "", italic: c.font.italic !== false }))
    );
  }
  return result;
}

/** First and last code point of a string. */
function edgeChars(text: string): { first: string; last: string } {
  const chars = Array.from(text);
  return { first: chars[0] ?? "", last: chars[chars.length - 1] ?? "" };
}

/**
 * Split a partly-italic word into italic/non-italic runs, keeping only the
 * boundaries that sit next to a split boundary character. Returns undefined when
 * no such boundary remains, which leaves the word treated as a whole (today's
 * behaviour, italic when mixed), or when the character data does not line up
 * with the word text.
 */
export function computeItalicRuns(
  wordText: string,
  wordStart: number,
  chars: WordCharItalics
): ItalicRun[] | undefined {
  if (chars.map((c) => c.text).join("") !== wordText) return undefined;

  const raw: ItalicRun[] = [];
  let offset = wordStart;
  for (const c of chars) {
    const last = raw[raw.length - 1];
    if (last && last.italic === c.italic) {
      last.end += c.text.length;
    } else {
      raw.push({ start: offset, end: offset + c.text.length, italic: c.italic });
    }
    offset += c.text.length;
  }

  const textOf = (r: ItalicRun) => wordText.slice(r.start - wordStart, r.end - wordStart);
  const runs: ItalicRun[] = [];
  for (const run of raw) {
    const prev = runs[runs.length - 1];
    if (!prev) {
      runs.push({ ...run });
      continue;
    }
    const atBoundary =
      isSplitBoundaryChar(edgeChars(textOf(prev)).last) ||
      isSplitBoundaryChar(edgeChars(textOf(run)).first);
    if (prev.italic === run.italic || !atBoundary) {
      // Not a split point: fold it in. A mixed run counts as italic, as before.
      prev.end = run.end;
      prev.italic = prev.italic || run.italic;
    } else {
      runs.push({ ...run });
    }
  }

  return runs.length > 1 ? runs : undefined;
}

// ── Word.js span extraction ───────────────────────────────────────

/**
 * Build the span-based formatting model from Word.js text ranges.
 *
 * Extracts per-word formatting flags (italic, bold, underline, strikethrough)
 * and computes paragraph-level aggregates. This is a pure data function with
 * no Word.js proxy references in its output — safe to store across Word.run
 * boundaries.
 *
 * Exported so `applyClassifiedStyles` can capture formatting *before*
 * paragraph styles are applied (which resets direct formatting).
 *
 * @param paragraphText - Full paragraph text.
 * @param wordRanges - Word.RangeCollection from getTextRanges (already loaded).
 * @returns ParagraphFormattingInfo for the rules engine.
 */

/**
 * Check whether a text string qualifies as uppercase for SC rules.
 * Returns true when the text contains at least 2 alphabetic characters
 * and all alphabetic characters are uppercase.
 */
function isUppercaseText(text: string): boolean {
  const alphaChars = text.replace(/[^a-zA-Z]/g, "");
  return alphaChars.length >= 2 && alphaChars === alphaChars.toUpperCase();
}

export function buildFormattingInfo(
  paragraphText: string,
  wordRanges: Word.RangeCollection,
  mixedItalicChars?: MixedItalicChars
): ParagraphFormattingInfo {
  const spans: FormattingSpan[] = [];
  let cursor = 0;

  for (let index = 0; index < wordRanges.items.length; index++) {
    const range = wordRanges.items[index];
    const text = range.text ?? "";
    if (!text) {
      continue;
    }

    let start = paragraphText.indexOf(text, cursor);
    if (start === -1) {
      start = cursor;
    }
    const end = start + text.length;

    const chars = mixedItalicChars?.get(index);
    const italicRuns =
      chars && paragraphText.slice(start, end) === text
        ? computeItalicRuns(text, start, chars)
        : undefined;

    spans.push({
      start,
      end,
      text,
      // Word.js returns null when formatting is mixed within a range.
      // Since ranges are word-level, null means partial coverage — treat
      // as true so bold+italic spans aren't silently dropped.
      italic: range.font.italic !== false,
      bold: range.font.bold !== false,
      underline: range.font.underline !== "None" && range.font.underline != null,
      strikethrough: range.font.strikeThrough !== false,
      characterStyle: normalizeStyle(range.style as string | undefined),
      // DISABLED: SmallCaps detection (rules disabled in rules-engine.ts)
      // smallCaps: range.font.smallCaps !== false,
      smallCaps: false,
      textIsUppercase: isUppercaseText(text),
      ...(italicRuns ? { italicRuns } : {}),
    });

    cursor = end;
  }

  const nonEmpty = spans.filter((s) => s.text.trim().length > 0);
  const italicWordCount = nonEmpty.filter((s) => s.italic).length;
  const totalWordCount = nonEmpty.length;
  // A partly-italic word counts as italic for the majority, but its roman part
  // means the paragraph is not all italic: I-6's paragraph-wide style would
  // cover that part too.
  const hasRomanRemainder = spans.some((s) => s.italicRuns?.some((r) => !r.italic));

  return {
    text: paragraphText,
    spans,
    majorityItalic: totalWordCount > 0 && italicWordCount > totalWordCount / 2,
    allItalic: totalWordCount > 0 && italicWordCount === totalWordCount && !hasRomanRemainder,
    allBold: totalWordCount > 0 && nonEmpty.every((s) => s.bold),
    allUppercase: totalWordCount > 0 && nonEmpty.every((s) => s.textIsUppercase),
  };
}

/**
 * Build a parallel array of Word.Range references from a loaded
 * RangeCollection, skipping empty-text ranges. The output is aligned
 * with the spans produced by {@link buildFormattingInfo} — index i in
 * this array corresponds to `ParagraphFormattingInfo.spans[i]`.
 *
 * Exported so the applicator can pair pre-captured formatting with
 * live Word Range proxies in the current Word.run context.
 *
 * @param wordRanges - Word.RangeCollection from getTextRanges (already loaded).
 * @returns Parallel Word.Range array for action execution.
 */
export function buildSpanRanges(wordRanges: Word.RangeCollection): Word.Range[] {
  const spanRanges: Word.Range[] = [];
  for (const range of wordRanges.items) {
    const text = range.text ?? "";
    if (!text) {
      continue;
    }
    spanRanges.push(range);
  }
  return spanRanges;
}

/**
 * Merged span ranges with only action-referenced indices populated.
 *
 * Indices stay aligned with `ParagraphFormattingInfo.spans`, so action
 * `spanIndex` values keep working; unreferenced slots are simply absent.
 */
type SparseSpanRanges = Array<Word.Range | undefined>;

/**
 * Merged-span indices the given actions will actually read.
 *
 * Mirrors the span lookups used by span-level apply/remove actions. Entity
 * actions are resolved independently from the paragraph range in Phase 3.
 */
function requiredSpanIndices(
  actions: CharacterStyleAction[],
  formatting: ParagraphFormattingInfo
): Set<number> {
  const required = new Set<number>();

  for (const action of actions) {
    if (action.type === "remove_formatting") {
      if (action.spanIndex != null) required.add(action.spanIndex);
      continue;
    }

    if (action.type !== "apply_style") continue;

    if (action.target === "span") {
      if (action.spanIndex != null) required.add(action.spanIndex);
      continue;
    }

    // Entity actions are resolved from the paragraph range via search() in
    // Phase 3, so they do not require any merged word/span ranges.
    if (action.target === "entity") continue;
  }

  return required;
}

/**
 * Merge consecutive spans that share an identical formatting fingerprint
 * (bold, italic, underline, strikethrough, characterStyle, smallCaps).
 *
 * Paragraph-level aggregates (majorityItalic, allItalic, allBold) are
 * word-count-based and must be computed BEFORE calling this — pass the
 * pre-merge ParagraphFormattingInfo and replace only its spans field.
 *
 * The returned spanRanges use expandTo() so each merged range covers the
 * full word run including the whitespace between constituent words.
 *
 * A group that starts or ends partway through a partly-italic word cannot be
 * built yet: its edge is found by searching inside that word, which needs a
 * sync. Those come back as `pendingTrims`, and their index stays empty until
 * {@link resolveSpanTrim} fills it after the Phase 3 sync.
 */
function mergeConsecutiveSpans(
  spans: FormattingSpan[],
  spanRanges: Word.Range[],
  paragraphText: string,
  required: ReadonlySet<number>
): { spans: FormattingSpan[]; spanRanges: SparseSpanRanges; pendingTrims: PendingSpanTrim[] } {
  if (!needsGrouping(spans)) return { spans, spanRanges, pendingTrims: [] };

  const groups = groupConsecutiveSpans(spans, paragraphText);

  // Only indices an action targets are materialised. `expandTo` is a Word
  // call and each resulting range gets tracked, and Word remaps every tracked
  // handle on every document mutation — so building the ones nothing references
  // costs quadratically for nothing. Holes are safe: both appliers already
  // skip a missing range.
  const mergedRanges: SparseSpanRanges = new Array(groups.length);
  const pendingTrims: PendingSpanTrim[] = [];
  for (let i = 0; i < groups.length; i++) {
    if (!required.has(i)) continue;
    const { start, end, startPiece, endPiece } = groups[i];
    if (startPiece || endPiece) {
      pendingTrims.push({
        index: i,
        startWord: spanRanges[start],
        endWord: spanRanges[end],
        singleWord: start === end,
        startSearch: startPiece && queuePieceSearch(spanRanges[start], spans[start].text, startPiece),
        endSearch: endPiece && queuePieceSearch(spanRanges[end], spans[end].text, endPiece),
      });
      continue;
    }
    mergedRanges[i] =
      start === end ? spanRanges[start] : spanRanges[start].expandTo(spanRanges[end]);
  }

  return { spans: groups.map((g) => g.span), spanRanges: mergedRanges, pendingTrims };
}

/** The part of a group that lies inside one word, and where it starts in that word. */
interface WordPiece {
  text: string;
  offsetInWord: number;
}

/** A word-local search for a {@link WordPiece}, loaded by the Phase 3 sync. */
interface PieceSearch {
  text: string;
  results: Word.RangeCollection;
  occurrenceIndex: number;
}

/** A merged span whose edge falls inside a partly-italic word. */
interface PendingSpanTrim {
  /** Merged span index the resolved range belongs to. */
  index: number;
  startWord: Word.Range;
  endWord: Word.Range;
  singleWord: boolean;
  /** Absent when the group starts at the word start, or the piece is unsearchable. */
  startSearch?: PieceSearch;
  endSearch?: PieceSearch;
}

/**
 * Occurrence number of `piece` starting exactly at `offset` in `text`, counting
 * non-overlapping matches as Word's search does, or -1. Exact rather than
 * {@link getEntityOccurrenceIndex}'s drift tolerance: inside one short word
 * (e.g. `a—a`) a few characters of drift would pick the wrong occurrence.
 */
function exactOccurrenceIndex(text: string, piece: string, offset: number): number {
  if (!piece) return -1;
  let occurrence = 0;
  let from = 0;
  for (;;) {
    const at = text.indexOf(piece, from);
    if (at === -1 || at > offset) return -1;
    if (at === offset) return occurrence;
    occurrence += 1;
    from = at + piece.length;
  }
}

function queuePieceSearch(
  wordRange: Word.Range,
  wordText: string,
  piece: WordPiece
): PieceSearch | undefined {
  const rejection = unsearchableReason(piece.text);
  const occurrenceIndex = rejection
    ? -1
    : exactOccurrenceIndex(wordText, piece.text, piece.offsetInWord);
  if (occurrenceIndex === -1) {
    console.warn(
      `[CharStyles] Cannot isolate "${piece.text}" in "${wordText}"` +
        (rejection ? ` — ${rejection}` : "") +
        "; using the whole word"
    );
    return undefined;
  }
  const results = wordRange.search(piece.text, { matchCase: true, matchWholeWord: false });
  results.load("items");
  return { text: piece.text, results, occurrenceIndex };
}

/**
 * Build the exact range of a trimmed merged span once its searches have loaded.
 * A piece that cannot be found falls back to its whole word — the behaviour
 * before partly-italic words were split — so a style is never dropped.
 */
function resolveSpanTrim(trim: PendingSpanTrim, lineIndex: number): Word.Range {
  const pick = (search: PieceSearch | undefined, whole: Word.Range): Word.Range => {
    if (!search) return whole;
    const found = search.results.items[search.occurrenceIndex];
    // Word's find treats straight and curly quotes alike, so check the match.
    if (found && found.text === search.text) return found;
    console.warn(
      `[CharStyles] Line ${lineIndex} | piece "${search.text}" not found in its word ` +
        `(occurrence=${search.occurrenceIndex}, matches=${search.results.items.length}); ` +
        "using the whole word"
    );
    return whole;
  };

  const head = pick(trim.startSearch, trim.startWord);
  if (trim.singleWord) return head;
  return head.expandTo(pick(trim.endSearch, trim.endWord));
}

/** Whether grouping can change anything: more than one span, or a word to split. */
function needsGrouping(spans: FormattingSpan[]): boolean {
  return spans.length > 1 || spans.some((s) => s.italicRuns);
}

/**
 * The grouping half of {@link mergeConsecutiveSpans}, with no Word calls.
 *
 * Splitting this out is what lets rules be evaluated before any word ranges are
 * fetched: the group boundaries depend only on the formatting fingerprints, so
 * the merged spans a paragraph *would* produce are computable from the snapshot
 * alone. `mergeConsecutiveSpans` then replays the same boundaries against live
 * ranges, keeping `spanRanges[i]` aligned with `spans[i]`.
 *
 * Grouping walks segments rather than words. A word is one segment, except a
 * partly-italic word (`italicRuns`), which is one segment per run — so a group
 * can start or end inside it. For `reinvigorate—the` with only `reinvigorate`
 * italic, the italic group ends after `reinvigorate` and `—the` stands alone
 * as a `partialWordRemainder`, which never merges into a neighbour's range.
 */
export function groupConsecutiveSpans(
  spans: FormattingSpan[],
  paragraphText: string
): SpanGroup[] {
  const fingerprint = (s: FormattingSpan): string =>
    `${s.bold}|${s.italic}|${s.underline}|${s.strikethrough}|${s.smallCaps}`;

  interface Segment {
    span: FormattingSpan;
    word: number;
    /** Covers only part of its word. */
    partial: boolean;
  }
  const segments: Segment[] = [];
  spans.forEach((span, word) => {
    const { italicRuns, ...wordSpan } = span;
    if (!italicRuns) {
      segments.push({ span, word, partial: false });
      return;
    }
    for (const run of italicRuns) {
      const text = paragraphText.slice(run.start, run.end);
      segments.push({
        span: {
          ...wordSpan,
          start: run.start,
          end: run.end,
          text,
          italic: run.italic,
          textIsUppercase: isUppercaseText(text),
          ...(run.italic ? {} : { partialWordRemainder: true }),
        },
        word,
        partial: true,
      });
    }
  });

  const groups: SpanGroup[] = [];
  let groupStart = 0;

  for (let i = 1; i <= segments.length; i++) {
    const endOfGroup =
      i === segments.length ||
      !!segments[i].span.partialWordRemainder ||
      !!segments[groupStart].span.partialWordRemainder ||
      fingerprint(segments[i].span) !== fingerprint(segments[groupStart].span);
    if (!endOfGroup) continue;

    const first = segments[groupStart];
    const last = segments[i - 1];
    if (i - groupStart === 1 && !first.partial) {
      groups.push({ span: first.span, start: first.word, end: first.word });
    } else {
      const mergedText = paragraphText.slice(first.span.start, last.span.end);
      const group: SpanGroup = {
        span: {
          ...first.span,
          end: last.span.end,
          text: mergedText,
          textIsUppercase: isUppercaseText(mergedText),
        },
        start: first.word,
        end: last.word,
      };
      if (first.partial) {
        const word = spans[first.word];
        const pieceEnd = first.word === last.word ? last.span.end : word.end;
        group.startPiece = {
          text: paragraphText.slice(first.span.start, pieceEnd),
          offsetInWord: first.span.start - word.start,
        };
      }
      if (last.partial && last.word !== first.word) {
        const word = spans[last.word];
        group.endPiece = { text: paragraphText.slice(word.start, last.span.end), offsetInWord: 0 };
      }
      groups.push(group);
    }
    groupStart = i;
  }

  return groups;
}

/** One merged span and the word indices it was built from. */
export interface SpanGroup {
  span: FormattingSpan;
  /** First and last word index, into the unmerged spans and word ranges. */
  start: number;
  end: number;
  /**
   * Set when the group starts partway into word `start`, or covers only part of
   * it when `start === end`: the range is then that piece, not the whole word.
   */
  startPiece?: WordPiece;
  /** Set when the group ends partway into word `end` (only when `start !== end`). */
  endPiece?: WordPiece;
}

/** Merged spans a paragraph would yield, computed from snapshot data alone. */
export function mergeSpansPurely(spans: FormattingSpan[], paragraphText: string): FormattingSpan[] {
  if (!needsGrouping(spans)) return spans;
  return groupConsecutiveSpans(spans, paragraphText).map((g) => g.span);
}

/**
 * Normalize a Word style name to a lowercase string or null.
 * Word returns "Normal" for the default paragraph style — treat it as no character style.
 */
export function normalizeStyle(style: string | undefined): string | null {
  if (!style) {
    return null;
  }
  const lower = style.toLowerCase();
  if (lower === "normal" || lower === "default paragraph font") {
    return null;
  }
  return lower;
}

// ── Action execution ──────────────────────────────────────────────

/**
 * Execute removal actions (remove_formatting) on the paragraph range.
 * Returns the count of applied changes.
 */
function applyFormattingRemoval(range: Word.Range, formatting: string): void {
  switch (formatting) {
    case "italic":
      range.font.italic = false;
      break;
    case "bold":
      range.font.bold = false;
      break;
    case "underline":
      range.font.underline = "None" as unknown as Word.UnderlineType;
      break;
    case "strikethrough":
      range.font.strikeThrough = false;
      break;
  }
}

function applyRemovalActions(
  lineIndex: number,
  range: Word.Range,
  actions: CharacterStyleAction[]
): number {
  let count = 0;
  let ccInserted = false;
  for (const action of actions) {
    if (action.type !== "remove_formatting") continue;
    if (action.target === "span") continue;

    applyFormattingRemoval(range, action.formatting);
    count += 1;

    if (action.tracked && action.actionId && !ccInserted) {
      ccInserted = true;
      const cc = range.insertContentControl();
      cc.tag = "character-style";
      cc.title = action.actionId;
    }
  }
  return count;
}

function applySpanRemovalActions(
  lineIndex: number,
  actions: CharacterStyleAction[],
  spanRanges: SparseSpanRanges
): number {
  let count = 0;
  const seenSpans = new Set<number>();
  for (const action of actions) {
    if (action.type !== "remove_formatting" || action.target !== "span") continue;
    if (action.spanIndex == null) continue;

    const wordRange = spanRanges[action.spanIndex];
    if (!wordRange) continue;

    applyFormattingRemoval(wordRange, action.formatting);
    count += 1;

    if (action.tracked && action.actionId && !seenSpans.has(action.spanIndex)) {
      seenSpans.add(action.spanIndex);
      const cc = wordRange.insertContentControl();
      cc.tag = "character-style";
      cc.title = action.actionId;
    }
  }
  return count;
}

/**
 * Apply paragraph-level style actions (target === "paragraph").
 * Wraps the paragraph range in a content control titled with the action's
 * tracked id so the review UI can select/revert it later.
 */
function applyParagraphStyleActions(
  lineIndex: number,
  range: Word.Range,
  actions: CharacterStyleAction[]
): number {
  let count = 0;
  for (const action of actions) {
    if (action.type === "apply_style" && action.target === "paragraph") {
      const cc = range.insertContentControl();
      cc.tag = CHAR_STYLE_CC_TAG;
      cc.title = action.actionId ?? `character-style-${lineIndex}-paragraph-${count}`;
      cc.style = action.style;
      count += 1;
    }
  }
  return count;
}

/**
 * Apply span-level style actions using spanIndex to directly look up
 * the Word range from the parallel spanRanges array.
 */
function applySpanStyleActions(
  lineIndex: number,
  actions: CharacterStyleAction[],
  spanRanges: SparseSpanRanges,
  formatting: ParagraphFormattingInfo
): number {
  let count = 0;
  for (const action of actions) {
    if (action.type !== "apply_style" || action.target !== "span") {
      continue;
    }
    if (action.spanIndex == null) {
      continue;
    }
    const wordRange = spanRanges[action.spanIndex];
    if (!wordRange) {
      continue;
    }
    const cc = wordRange.insertContentControl();
    cc.tag = CHAR_STYLE_CC_TAG;
    cc.title = action.actionId ?? `character-style-${lineIndex}-span-${action.spanIndex}`;
    cc.style = action.style;
    count += 1;
  }
  return count;
}

/**
 * Apply character styles to entity-targeted actions using exact Word ranges resolved
 * during Phase 3. Phase 3 searches within the entity's containing formatting range,
 * so punctuation attached to the same Word/getTextRanges span is not included in the
 * content control unless it is part of action.targetText.
 */
function applyEntityStyleActions(
  lineIndex: number,
  actions: CharacterStyleAction[],
  entityRanges: Map<CharacterStyleAction, Word.Range>
): number {
  let count = 0;
  for (const action of actions) {
    if (action.type !== "apply_style" || action.target !== "entity") continue;
    if (!action.targetText) continue;

    const targetRange = entityRanges.get(action);
    if (!targetRange) {
      console.warn(
        `[CharStyles] Line ${lineIndex} | SKIP entity "${action.targetText}" — exact Word range not found`
      );
      continue;
    }

    const cc = targetRange.insertContentControl();
    cc.tag = CHAR_STYLE_CC_TAG;
    cc.title = action.actionId ?? `character-style-${lineIndex}-${count}`;
    cc.style = action.style;
    count++;
  }
  return count;
}

/** ANY CHANGES TO THIS FUNCTION MUST BE THOROUGHLY TESTED FOR ALL CHARACTER STYLE SCENARIOS****
 * Drop apply_style actions whose content controls would partially overlap
 * another action's content control — Word rejects partially overlapping CCs
 * with GeneralException on sync. Entity actions win over span actions
 * covering the same text; for duplicate targets the first action wins.
 * 
 * Also since this function drops some style actions on overlapped spans, it is not working for some scenarios. 
 * Suppose for example a span contains - 
 * The New York Times Book Review,
 * The Washington Post,
 * The Chicago Tribune,
 * New York magazine,
 * Elle,
 * Glamour,
 * Allure,
 * Real Simple
 * And the bold rule action identifies the whole span, but because span overlaps "The New York Times Book Review"—and several other entities
 * —the whole bold action for that span is thrown away in this function.
 * This is needs to be addressed either in ApplySpanStyleActions or in this function in the next release
 */
function filterOverlappingCcActions(
  actions: CharacterStyleAction[],
  formatting: ParagraphFormattingInfo
): CharacterStyleAction[] {
  type Interval = {
    start: number;
    end: number;
    action: CharacterStyleAction;
  };

  const overlaps = (
    aStart: number,
    aEnd: number,
    bStart: number,
    bEnd: number
  ): boolean => aStart < bEnd && aEnd > bStart;

  // Entity content controls are now created on exact searched ranges, so
  // entity/entity conflicts must be evaluated using exact character offsets.
  // Two separate entities are allowed even when they live in the same merged
  // formatting span.
  const acceptedEntities = new Set<CharacterStyleAction>();
  const entityIntervals: Interval[] = [];

  for (const action of actions) {
    if (
      action.type !== "apply_style" ||
      action.target !== "entity" ||
      action.targetStartIndex == null ||
      !action.targetText
    ) {
      continue;
    }

    const start = action.targetStartIndex;
    const end = start + action.targetText.length;

    const conflictsWithExistingEntity = entityIntervals.some((existing) =>
      overlaps(start, end, existing.start, existing.end)
    );

    // if (conflictsWithExistingEntity) {
    //   console.log(
    //     `[CharStyles] Skipping overlapping entity "${action.targetText}" [${start}-${end}]`
    //   );
    //   continue;
    // }

    acceptedEntities.add(action);
    entityIntervals.push({ start, end, action });
  }

  const seenSpanIndices = new Set<number>();

  return actions.filter((action) => {
    if (action.type !== "apply_style") {
      return true;
    }

    if (action.target === "entity") {
      return acceptedEntities.has(action);
    }

    if (action.target === "span" && action.spanIndex != null) {
      if (seenSpanIndices.has(action.spanIndex)) {
        return false;
      }

      const span = formatting.spans[action.spanIndex];
      if (!span) {
        return false;
      }

      // Entity actions take precedence, but only when the span actually overlaps
      // an accepted entity's exact character range.
      const overlapsEntity = entityIntervals.some((entity) =>
        overlaps(span.start, span.end, entity.start, entity.end)
      );

      if (overlapsEntity) {
        return false;
      }

      seenSpanIndices.add(action.spanIndex);
      return true;
    }

    return true;
  });
}


/**
 * Return the zero-based occurrence number of targetText whose start offset
 * equals targetStartIndex in paragraphText. Returns -1 when the supplied
 * entity offset does not correspond to an exact occurrence of targetText.
 */
function getEntityOccurrenceIndex(
  paragraphText: string,
  targetText: string,
  targetStartIndex: number
): number {
  let occurrenceIndex = 0;
  let searchFrom = 0;

  const MAX_OFFSET_DRIFT = 3;

  while (searchFrom <= paragraphText.length - targetText.length) {
    const foundAt = paragraphText.indexOf(targetText, searchFrom);

    if (foundAt === -1) {
      return -1;
    }

    if (Math.abs(foundAt - targetStartIndex) <= MAX_OFFSET_DRIFT) {
      return occurrenceIndex;
    }

    occurrenceIndex += 1;
    searchFrom = foundAt + 1;
  }

  return -1;
}

/** Word's "Find what" limit, which the JS search API shares. */
const WORD_SEARCH_MAX_LENGTH = 255;

/**
 * Leading/trailing chunk length used to bracket an entity longer than the
 * search limit: under the limit with margin, and long enough that a chunk is
 * unique within its paragraph.
 */
const LONG_ENTITY_ANCHOR_LENGTH = 250;

/**
 * Describe why Word's find engine would reject `text` as a search string
 * whatever its length, or null when it is usable. Word rejects the whole
 * request batch with SearchStringInvalidOrTooLong, so one bad entity would
 * otherwise abort the entire run. Over-length text is bracketed by anchors
 * rather than rejected.
 */
function unsearchableReason(text: string): string | null {
  const control = /[\r\n\v\f\t\u0007\u001E\u001F]/.exec(text);
  if (control) {
    const code = control[0].codePointAt(0)!.toString(16).padStart(4, "0");
    return `contains control character U+${code.toUpperCase()} at index ${control.index}`;
  }
  // "^" opens an escape sequence (^p, ^t, ^13); an unintended one is rejected.
  if (text.includes("^")) {
    return `contains "^", which Word reads as a find escape sequence`;
  }
  return null;
}

/**
 * Collapse whitespace so a Word range's text can be compared against a
 * classifier hint. Word reports non-breaking spaces and runs of spaces as it
 * stores them, which the hint text does not always preserve.
 */
function normalizeEntityText(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function attachTrackedActionIds(
  actions: CharacterStyleAction[],
  paragraphIndex: number
): CharacterStyleAction[] {
  const removalIds = new Map<string, string>();
  return actions.map((action) => {
    if (!action.tracked || action.actionId) {
      return action;
    }

    if (action.type === "remove_formatting") {
      const key = action.target === "span" ? `span:${action.spanIndex}` : "paragraph";
      let actionId = removalIds.get(key);
      if (!actionId) {
        actionId = `${CHAR_STYLE_TAG_PREFIX}${String(paragraphIndex).padStart(10, "0")}:${generateUUID()}`;
        removalIds.set(key, actionId);
      }
      return { ...action, actionId };
    }

    const uuid = generateUUID();
    const actionId = `${CHAR_STYLE_TAG_PREFIX}${String(paragraphIndex).padStart(10, "0")}:${uuid}`;

    return { ...action, actionId };
  });
}

// ── Pure planning ─────────────────────────────────────────────────

/** What a paragraph's plan needs from Word in order to be executed. */
export type PlanNeed = "nothing" | "paragraphRange" | "wordRanges";

export interface ParagraphPlan {
  /** Merged formatting, span-index-aligned with the actions below. */
  formatting: ParagraphFormattingInfo;
  actions: CharacterStyleAction[];
  need: PlanNeed;
}

/**
 * Decide what a paragraph's character styles should be, using no Word API calls.
 *
 * Rule evaluation only ever reads the snapshot, so it can run before any word
 * ranges are fetched — and 87% of paragraphs turn out to need none at all. The
 * `need` field is what the caller uses to skip `getTextRanges` for those.
 */
export function planParagraphActions(
  snapshotFormatting: ParagraphFormattingInfo,
  line: Pick<ClassificationResponse["lines"][number], "index" | "style" | "entity_hints">,
  paragraphCategory: string,
  characterStyles: CharacterStyleProfileEntry[]
): ParagraphPlan {
  const paragraphText = snapshotFormatting.text;
  const formatting: ParagraphFormattingInfo = {
    ...snapshotFormatting,
    spans: mergeSpansPurely(snapshotFormatting.spans, paragraphText),
  };

  const entityHints = (line.entity_hints ?? []).filter(
    (h) => Number(h.confidence) >= ENTITY_HINT_CONFIDENCE_THRESHOLDS[h.type]
  );

  const ruleContext: RuleContext = {
    paragraphStyle: line.style ?? "",
    paragraphCategory,
    paragraphText,
    formatting,
    entityHints,
    characterStyles,
  };

  const rawActions = evaluateAllRules(ruleContext);
  const filtered = filterOverlappingCcActions(rawActions, formatting);
  const actions = attachTrackedActionIds(filtered, line.index);

  return { formatting, actions, need: planNeed(actions) };
}

/**
 * Only span-targeted actions index into `spanRanges` and require the per-word
 * `getTextRanges` call. Entity actions are resolved by paragraph search using
 * their exact text and targetStartIndex.
 */
function planNeed(actions: CharacterStyleAction[]): PlanNeed {
  if (actions.length === 0) return "nothing";
  const needsSpans = actions.some(
    (a) =>
      (a.type === "apply_style" && a.target === "span") ||
      (a.type === "remove_formatting" && a.target === "span")
  );
  return needsSpans ? "wordRanges" : "paragraphRange";
}

// ── Orchestrator ──────────────────────────────────────────────────

/**
 * Orchestrate character style rule evaluation and Word.js updates for
 * all paragraphs in a classification response.
 *
 * Execution flow:
 *   1. Load profile and style definitions
 *   2. Batch-load paragraph text, formatting spans (getTextRanges)
 *   3. For each paragraph: build ParagraphFormattingInfo → evaluate rules → queue actions
 *   4. Execute actions: removals → span styles → entity search + sync → entity styles
 *   5. Final sync to flush all changes
 *
 * @param response - Classification response with styled lines and entity hints.
 * @param profileName - Name of the style profile to load.
 * @param formattingSnapshot - Optional pre-captured formatting state, built before
 *   paragraph styles were applied. When provided, the snapshot's formatting data
 *   is used for rule evaluation instead of re-reading from the document (where
 *   direct formatting may have been reset by paragraph style application).
 * @param perf - Optional perf session for per-sync timing attribution.
 * @param onProgress - Optional per-step progress reporter for the UI.
 * @returns Counts of applied and skipped paragraphs.
 */
export async function applyCharacterStyles(
  response: ClassificationResponse,
  profileName: string,
  formattingSnapshot?: FormattingSnapshot,
  localIdToTagMap?: Map<string, string>,
  perf?: PerfSession,
  onProgress?: ApplyProgressReporter
): Promise<CharStyleApplicationResult> {
  if (!profileName) {
    return { applied: 0, skipped: 0, actionRecords: [], characterStyles: [] };
  }

  const loadProfile = async () => {
    const p = await loadStyleProfile(profileName);
    const m = await buildStyleDefinitionMap(profileName);
    return { p, m };
  };
  const { p: profile, m: styleDefinitionMap } = await (perf
    ? perf.time("charStyle:loadProfile", loadProfile)
    : loadProfile());
  const characterStyles = profile.character_styles ?? [];

  if (characterStyles.length === 0) {
    return { applied: 0, skipped: 0, actionRecords: [], characterStyles: [] };
  }

  return Word.run(async (context) => {
    const sync = (name: string, meta?: () => Record<string, number | string | boolean>) =>
      perf ? perf.timedSync(context, name, meta) : context.sync();
    let appliedCount = 0;
    let skippedCount = 0;
    const actionRecords: CharStyleActionRecord[] = [];

    // Phase 0: Remove character-style content controls left by prior runs so
    // newly inserted ones cannot partially overlap them (partial CC overlap
    // causes GeneralException on sync). Matching by tag rather than excluding
    // known tags leaves the user's own content controls — form fields,
    // dropdowns, other add-ins' — untouched.
    // These three loads were one sync costing 19.5s on a document with zero
    // stale controls, which said nothing about which load was responsible.
    // Bundling them is what made the number unreadable, so they are timed apart
    // — the same reason pass 1 was split.
    const phase0Progress = new StepProgress("Checking existing character styles", 3, onProgress);

    const staleCCs = context.document.contentControls.getByTag(CHAR_STYLE_CC_TAG);
    staleCCs.load("items");
    await sync("charStyle:phase0LoadControls");
    phase0Progress.set(1);

    // One collection load replaces a getParagraphByUniqueLocalId call per
    // planned paragraph in Phase 2.
    const allParagraphs = context.document.body.paragraphs;
    allParagraphs.load("items/uniqueLocalId");
    await sync("charStyle:phase0LoadParagraphs", () => ({
      paragraphs: allParagraphs.items.length,
    }));
    phase0Progress.set(2);

    // Diagnostic only — nothing below branches on the revision count. Every CC
    // delete and insert is a revision while track changes is on, and each one
    // Word carries costs more as the pile grows, so the starting count is what
    // distinguishes a slow re-run from a slow document. If this load is what
    // costs the 19.5s, delete it: it is a measurement, not a requirement.
    const openingChanges = context.document.body.getTrackedChanges();
    openingChanges.load("items/type");
    await sync("charStyle:phase0LoadTrackedChanges");
    phase0Progress.done();

    perf?.note("charStyle:openingTrackedChanges", {
      trackedChanges: openingChanges.items.length,
    });

    const paragraphsByLocalId = new Map<string, Word.Paragraph>();
    for (const paragraph of allParagraphs.items) {
      if (paragraph.uniqueLocalId) {
        paragraphsByLocalId.set(paragraph.uniqueLocalId, paragraph);
      }
    }

    // office-addins can't follow load state through getByTag's method receiver;
    // the load and its sync are both directly above.
    /* eslint-disable office-addins/call-sync-after-load, office-addins/call-sync-before-read */
    const stale = staleCCs.items;
    /* eslint-enable office-addins/call-sync-after-load, office-addins/call-sync-before-read */

    perf?.note("charStyle:phase0Found", { staleControls: stale.length });
    if (stale.length > 0) {
      const deleteProgress = new StepProgress(
        "Removing previous character styles",
        stale.length,
        onProgress
      );
      let deleteBatchIndex = 0;

      // These proxies were resolved by one collection load and now have to
      // survive syncs that mutate the document, which a single delete sync
      // never required of them. Tracking is what keeps their paths valid across
      // a mutation — the same reason Phase 3 tracks its span ranges.
      for (const cc of stale) {
        context.trackedObjects.add(cc);
      }

      for (let start = 0; start < stale.length; start += PHASE0_DELETES_PER_BATCH) {
        const batch = stale.slice(start, start + PHASE0_DELETES_PER_BATCH);
        for (const cc of batch) {
          cc.delete(true); // keepContent = true — preserves text
        }
        deleteBatchIndex += 1;
        const label = `charStyle:phase0DeleteBatch${String(deleteBatchIndex).padStart(2, "0")}`;
        try {
          await sync(label, () => ({ deleted: batch.length, cumulative: start + batch.length }));
        } catch (batchError) {
          // Leaving a stale control behind risks a partial overlap with a new
          // insert later, so log it rather than swallow it — but the remaining
          // batches are independent and still worth attempting.
          console.warn(`[CharStyles] stale control batch ${deleteBatchIndex} failed:`, batchError);
        }
        deleteProgress.set(start + batch.length);
      }

      // Released before the passes, not at the end of the run: Word remaps every
      // tracked handle on each mutation, and the passes are thousands of
      // mutations. Failing to untrack costs speed, not correctness — the run's
      // teardown releases them anyway — so a failure here must not abort.
      try {
        for (const cc of stale) {
          context.trackedObjects.remove(cc);
        }
      } catch (untrackError) {
        console.warn("[CharStyles] could not untrack stale controls:", untrackError);
      }

      perf?.note("charStyle:phase0DeleteTotal", {
        deleted: stale.length,
        batches: deleteBatchIndex,
      });
    }

    // Phase 1: Decide every paragraph's actions with no Word calls. Only the
    // paragraphs whose plan targets spans or entities need per-word ranges,
    // which on real manuscripts is a small minority — fetching ranges
    // document-wide is what made this stage dominate the run.
    interface PlannedLine {
      line: ClassificationResponse["lines"][number];
      /** Unmerged snapshot spans, needed to replay grouping against live ranges. */
      snapshotSpans: FormattingSpan[];
      plan: ParagraphPlan;
    }
    const planned: PlannedLine[] = [];

    // Fallback for callers that supply no snapshot: formatting has to be read
    // live, which needs the expensive per-word font load. `applyClassifiedStyles`
    // always writes an entry, so in the normal pipeline this set is empty.
    const legacyRanges = new Map<string, Word.RangeCollection>();
    const legacyParagraphs = new Map<string, Word.Paragraph>();
    const legacyMixedChars = new Map<string, MixedItalicChars>();
    const legacyLines = response.lines.filter(
      (line) => line.paragraph_id && !formattingSnapshot?.has(line.paragraph_id)
    );

    if (legacyLines.length > 0) {
      for (const line of legacyLines) {
        const paragraph = context.document.getParagraphByUniqueLocalId(line.paragraph_id!);
        paragraph.load("text");
        const wordRanges = paragraph.getRange().getTextRanges(WORD_DELIMITERS, true);
        wordRanges.load(
          "items/text, items/font/italic, items/font/bold, items/font/underline, items/font/strikeThrough, items/style"
        );
        legacyRanges.set(line.paragraph_id!, wordRanges);
        legacyParagraphs.set(line.paragraph_id!, paragraph);
      }
      await sync("charStyle:legacyFormattingLoad", () => ({ paragraphs: legacyLines.length }));

      const pendingChars = new Map<string, Map<number, Word.RangeCollection>>();
      for (const [paragraphId, wordRanges] of legacyRanges) {
        const pending = queueMixedItalicCharLoads(wordRanges);
        if (pending.size > 0) pendingChars.set(paragraphId, pending);
      }
      if (pendingChars.size > 0) {
        await sync("charStyle:legacyMixedItalicLoad", () => ({ paragraphs: pendingChars.size }));
        for (const [paragraphId, pending] of pendingChars) {
          legacyMixedChars.set(paragraphId, readMixedItalicChars(pending));
        }
      }
    }

    const planAll = () => {
      for (const line of response.lines) {
        if (!line.paragraph_id) {
          skippedCount += 1;
          continue;
        }

        if (line.locked) {
          skippedCount += 1;
          continue;
        }

        const legacy = legacyRanges.get(line.paragraph_id);
        const snapshot = legacy
          ? buildFormattingInfo(
              legacyParagraphs.get(line.paragraph_id)?.text ?? "",
              legacy,
              legacyMixedChars.get(line.paragraph_id)
            )
          : formattingSnapshot?.get(line.paragraph_id);
        if (!snapshot) {
          skippedCount += 1;
          continue;
        }

        const category = styleDefinitionMap.get(line.style ?? "")?.category ?? "Unknown";
        const plan = planParagraphActions(snapshot, line, category, characterStyles);
        if (plan.need === "nothing") {
          continue;
        }

        planned.push({ line, snapshotSpans: snapshot.spans, plan });
      }
    };

    const planProgress = new StepProgress("Planning character styles", 1, onProgress);

    if (perf) {
      await perf.time("charStyle:ruleEval", planAll, () => ({
        lines: response.lines.length,
        planned: planned.length,
        needWordRanges: planned.filter((p) => p.plan.need === "wordRanges").length,
        actions: planned.reduce((sum, p) => sum + p.plan.actions.length, 0),
      }));
    } else {
      planAll();
    }

    // Phase 2: Fetch Word ranges, for the planned paragraphs only.
    interface LoadedTarget extends PlannedLine {
      range: Word.Range;
      wordRanges?: Word.RangeCollection;
    }
    const loaded: LoadedTarget[] = [];

    let lookupMisses = 0;

    for (const entry of planned) {
      const paragraphId = entry.line.paragraph_id!;
      const paragraph = paragraphsByLocalId.get(paragraphId);
      if (!paragraph) {
        lookupMisses += 1;
        skippedCount += 1;
        continue;
      }
      // `content` excludes the paragraph mark. A paragraph-target content
      // control that wrapped the mark would become a block-level control, so
      // selecting it from the review list highlights the whole paragraph plus
      // the mark instead of the styled text.
      const range = paragraph.getRange(Word.RangeLocation.content);
      if (entry.plan.need !== "wordRanges") {
        loaded.push({ ...entry, range });
        continue;
      }

      const existing = legacyRanges.get(paragraphId);
      if (existing) {
        loaded.push({ ...entry, range, wordRanges: existing });
        continue;
      }
      // getTextRanges splits by whitespace → one range per word. trimSpacing
      // excludes the paragraph mark so ranges align with the snapshot's spans.
      // Formatting comes from the snapshot, so only text is loaded here.
      const wordRanges = range.getTextRanges(WORD_DELIMITERS, true);
      wordRanges.load("items/text");
      loaded.push({ ...entry, range, wordRanges });
    }

    await sync("charStyle:wordRangeScan", () => ({
      paragraphs: loaded.filter((t) => t.wordRanges).length,
      ofLines: response.lines.length,
      wordRanges: loaded.reduce((sum, t) => sum + (t.wordRanges?.items.length ?? 0), 0),
      lookupMisses,
    }));

    // Phase 3: Bind span indices to live Word ranges and resolve exact entity ranges.
    interface PendingEntitySearch {
      action: CharacterStyleAction;
      results: Word.RangeCollection;
      occurrenceIndex: number;
      /**
       * Set only for an entity too long to search in one call. `results` then
       * holds matches for its leading anchor and this its trailing one; the two
       * are expanded into a single range once both have resolved.
       */
      tail?: { results: Word.RangeCollection; occurrenceIndex: number };
    }

    interface PreparedTarget {
      line: ClassificationResponse["lines"][number];
      range: Word.Range;
      actions: CharacterStyleAction[];
      spanRanges: SparseSpanRanges;
      formatting: ParagraphFormattingInfo;
      pendingEntitySearches: PendingEntitySearch[];
      entityRanges: Map<CharacterStyleAction, Word.Range>;
      /** Span ranges with an edge inside a partly-italic word, filled after the Phase 3 sync. */
      pendingTrims: PendingSpanTrim[];
    }
    const prepared: PreparedTarget[] = [];
    let trackedCount = 0;

    for (const target of loaded) {
      let spanRanges: SparseSpanRanges = [];
      let pendingTrims: PendingSpanTrim[] = [];
      const pendingEntitySearches: PendingEntitySearch[] = [];

      if (target.wordRanges) {
        const rawSpanRanges = buildSpanRanges(target.wordRanges);
        // The plan's span indices came from the snapshot's word ranges. A count
        // mismatch means the document changed underneath us, so those indices no
        // longer identify the same words — applying anyway styles the wrong text.
        if (rawSpanRanges.length !== target.snapshotSpans.length) {
          console.warn(
            `[CharStyles] Line ${target.line.index} | SKIP — word range count changed since snapshot (${target.snapshotSpans.length} → ${rawSpanRanges.length})`
          );
          skippedCount += 1;
          continue;
        }
        const merged = mergeConsecutiveSpans(
          target.snapshotSpans,
          rawSpanRanges,
          target.plan.formatting.text,
          requiredSpanIndices(target.plan.actions, target.plan.formatting)
        );
        spanRanges = merged.spanRanges;
        pendingTrims = merged.pendingTrims;

        // Span/entity ranges are used after the pass-1 sync mutates the
        // document (CC inserts); tracking keeps their proxy paths valid.
        // Word remaps every tracked handle on each mutation, so this is
        // deliberately limited to the ranges actions reference.
        for (const r of spanRanges) {
          if (!r) continue;
          context.trackedObjects.add(r);
          trackedCount += 1;
        }
      }


      // Resolve entity actions independently of formatting spans. Search the
      // paragraph content for all occurrences, then use targetStartIndex to pick
      // the exact occurrence intended by the entity hint.
      for (const action of target.plan.actions) {
        if (
          action.type !== "apply_style" ||
          action.target !== "entity" ||
          action.targetStartIndex == null ||
          !action.targetText
        ) {
          continue;
        }

        const entityText = action.targetText;
        const rejection = unsearchableReason(entityText);
        if (rejection) {
          console.warn(
            `[CharStyles] Line ${target.line.index} | SKIP entity ` +
              `"${entityText.slice(0, 80)}" @${action.targetStartIndex} — ${rejection}`
          );
          skippedCount += 1;
          continue;
        }

        // Word caps a search string, so an entity longer than that is bracketed
        // by its two ends instead: the leading and trailing anchors are searched
        // separately and expanded into one range, which covers the middle
        // without needing to search for it.
        const overLong = entityText.length > WORD_SEARCH_MAX_LENGTH;
        const headText = overLong
          ? entityText.slice(0, LONG_ENTITY_ANCHOR_LENGTH)
          : entityText;

        const occurrenceIndex = getEntityOccurrenceIndex(
          target.plan.formatting.text,
          headText,
          action.targetStartIndex
        );

        if (occurrenceIndex === -1) {
          console.warn(
            `[CharStyles] Line ${target.line.index} | Could not map entity ` +
              `"${entityText.slice(0, 80)}" to start index ${action.targetStartIndex}`
          );
          continue;
        }

        let tail: PendingEntitySearch["tail"];
        if (overLong) {
          const tailText = entityText.slice(-LONG_ENTITY_ANCHOR_LENGTH);
          const tailStartIndex =
            action.targetStartIndex + entityText.length - tailText.length;
          const tailOccurrenceIndex = getEntityOccurrenceIndex(
            target.plan.formatting.text,
            tailText,
            tailStartIndex
          );

          if (tailOccurrenceIndex === -1) {
            console.warn(
              `[CharStyles] Line ${target.line.index} | SKIP entity ` +
                `"${entityText.slice(0, 80)}" — could not map trailing anchor to ` +
                `start index ${tailStartIndex}`
            );
            skippedCount += 1;
            continue;
          }

          const tailResults = target.range.search(tailText, {
            matchCase: true,
            matchWholeWord: false,
          });
          tailResults.load("items");
          tail = { results: tailResults, occurrenceIndex: tailOccurrenceIndex };

          // An entity this long is unusual enough to describe in full: which
          // rule produced it, and how much of the paragraph it covers.
          console.warn(
            `[CharStyles] Line ${target.line.index} | over-long entity, bracketing with anchors | ` +
              `len=${entityText.length} rule=${action.ruleId ?? "?"} ` +
              `entityType=${action.entityType ?? "-"} style=${action.style} ` +
              `paraStyle=${target.line.style ?? "-"} paraId=${target.line.paragraph_id ?? "-"} ` +
              `paraLen=${target.plan.formatting.text.length} start=${action.targetStartIndex}`
          );
          // Head and tail on their own line because Safari's console truncates a
          // long string, dropping exactly the part a tester needs to paste back.
          // console.log(
          //   `[CharStyles] Line ${target.line.index} | anchors | ` +
          //     `${JSON.stringify(headText.slice(0, 120))} … ${JSON.stringify(tailText.slice(-120))}`
          // );
        }

        const results = target.range.search(headText, {
          matchCase: true,
          matchWholeWord: false,
        });
        results.load("items");
        pendingEntitySearches.push({ action, results, occurrenceIndex, tail });
      }

      actionRecords.push({
        paragraphTag: localIdToTagMap?.get(target.line.paragraph_id!) ?? "",
        paragraphId: target.line.paragraph_id!,
        paragraphStyle: target.line.style ?? "",
        actions: target.plan.actions,
        formattingInfo: target.plan.formatting,
      });

      prepared.push({
        line: target.line,
        range: target.range,
        actions: target.plan.actions,
        spanRanges,
        formatting: target.plan.formatting,
        pendingEntitySearches,
        entityRanges: new Map<CharacterStyleAction, Word.Range>(),
        pendingTrims,
      });
    }

    // Execute in two synced passes: paragraph-level content controls must be
    // committed before span/entity controls are inserted inside them —
    // queueing an insert nested within a same-batch insert (or partially
    // overlapping one) fails with GeneralException on sync.
    perf?.note("charStyle:phase3Prepared", {
      paragraphs: prepared.length,
      trackedRanges: trackedCount,
    });

    // Word reports a rejected batch as a bare GeneralException — on Mac
    // `debugInfo` carries no `errorLocation`, so the only way to identify the
    // offending insert is to dump what the failed sync had queued.
    //
    // A failed batch is never retried: Word executes a request's operations in
    // order and reports only the first error, so earlier inserts may already
    // have been applied and replaying them would duplicate them. That makes
    // `applied` and `skipped` approximate whenever a batch fails.
    // One short line per paragraph rather than one JSON blob: Safari's console
    // truncates a long string, and a tester pasting the log loses exactly the
    // part that matters.
    const describeFailedBatch = (items: PreparedTarget[], pass: 1 | 2): string[] =>
      items.map((p) => {
        const actions = p.actions
          .filter((a) =>
            pass === 1
              ? (a.type === "remove_formatting" && a.target !== "span") ||
                (a.type === "apply_style" && a.target === "paragraph")
              : (a.type === "remove_formatting" && a.target === "span") ||
                (a.type === "apply_style" && a.target !== "paragraph")
          )
          .map((a) => {
            const rule = a.ruleId ?? "?";
            if (a.type === "remove_formatting") {
              return `remove ${a.formatting} (${rule})`;
            }
            if (a.target === "span") {
              return `span[${a.spanIndex}] ${a.style} (${rule})`;
            }
            const entity = a.entityType ? ` ${a.entityType}` : "";
            return `${a.target} ${a.style} @${a.targetStartIndex} "${a.targetText}"${entity} (${rule})`;
          });
        const text = p.formatting.text.slice(0, 80);
        return `line ${p.line.index} [${p.line.style ?? "-"}] "${text}" — ${actions.join(" | ") || "(no action)"}`;
      });

    // As one sync, pass 1 was 21% of a run and reported a single number that
    // moved *opposite* to its operation count across traces (540ms/op at 362
    // ops, 77ms/op at 106, 294ms/op at 49) — so it was never per-operation.
    // It conflated two unrelated bodies of work, which are now timed apart:
    // this sync flushes only what Phase 3 queued (expandTo calls and tracked-
    // object registrations), and the batches below flush the pass-1 actions.
    await sync("charStyle:phase3Sync", () => ({ trackedRanges: trackedCount }));

    // Resolve entity search collections loaded by the Phase 3 sync. Each search
    // returns every matching occurrence in the paragraph; occurrenceIndex, derived
    // from targetStartIndex, selects the exact entity intended by the hint.
    let exactEntityRangeCount = 0;
    // Bracketed entities are verified after the next sync, once the expanded
    // range's text has loaded.
    const bracketedEntities: Array<{
      target: PreparedTarget;
      action: CharacterStyleAction;
      range: Word.Range;
      expected: string;
    }> = [];

    let trimmedSpanRangeCount = 0;
    for (const p of prepared) {
      // Italic spans whose edge falls inside a partly-italic word: their word-local
      // searches loaded with the entity searches, so the exact range is buildable now.
      for (const trim of p.pendingTrims) {
        const range = resolveSpanTrim(trim, p.line.index);
        p.spanRanges[trim.index] = range;
        context.trackedObjects.add(range);
        trackedCount += 1;
        trimmedSpanRangeCount += 1;
      }

      for (const pending of p.pendingEntitySearches) {
        const { action, results, occurrenceIndex, tail } = pending;
        if (action.type !== "apply_style" || !action.targetText) continue;

        const headRange = results.items[occurrenceIndex];
        if (!headRange) {
          console.warn(
            `[CharStyles] Line ${p.line.index} | Exact entity range not found for ` +
              `"${action.targetText.slice(0, 80)}" @${action.targetStartIndex} | ` +
              `occurrence=${occurrenceIndex} | Word matches=${results.items.length}`
          );
          continue;
        }

        let exactRange = headRange;
        if (tail) {
          const tailRange = tail.results.items[tail.occurrenceIndex];
          if (!tailRange) {
            console.warn(
              `[CharStyles] Line ${p.line.index} | SKIP entity ` +
                `"${action.targetText.slice(0, 80)}" — trailing anchor range not found | ` +
                `occurrence=${tail.occurrenceIndex} | Word matches=${tail.results.items.length}`
            );
            skippedCount += 1;
            continue;
          }

          exactRange = headRange.expandTo(tailRange);
          exactRange.load("text");
          bracketedEntities.push({
            target: p,
            action,
            range: exactRange,
            expected: action.targetText,
          });
        }

        // console.log(
        //   `[CharStyles] Line ${p.line.index} | Resolved entity ` +
        //     `"${action.targetText.slice(0, 80)}" @${action.targetStartIndex} -> ` +
        //     `occurrence ${occurrenceIndex + 1}/${results.items.length}` +
        //     (tail ? " (anchored)" : "")
        // );

        p.entityRanges.set(action, exactRange);
        context.trackedObjects.add(exactRange);
        trackedCount += 1;
        exactEntityRangeCount += 1;
      }
    }

    // Tracking is queued after the search results are materialized, so commit it
    // before Pass 1 mutates the document. This is one global sync, not one per entity.
    if (exactEntityRangeCount > 0 || trimmedSpanRangeCount > 0) {
      await sync("charStyle:phase3TrackEntityRanges", () => ({
        entityRanges: exactEntityRangeCount,
        trimmedSpanRanges: trimmedSpanRangeCount,
        trackedRanges: trackedCount,
      }));
    }

    // A bracketed range spans from one anchor to the other, so a wrong anchor
    // occurrence would style an arbitrary stretch of the paragraph rather than
    // simply missing. Comparing the expanded text against the hint is the only
    // check that catches that, and the sync above has already loaded it.
    for (const bracketed of bracketedEntities) {
      const actual = normalizeEntityText(bracketed.range.text ?? "");
      const expected = normalizeEntityText(bracketed.expected);
      if (actual === expected) continue;

      bracketed.target.entityRanges.delete(bracketed.action);
      skippedCount += 1;
      console.warn(
        `[CharStyles] Line ${bracketed.target.line.index} | SKIP entity — ` +
          `bracketed range does not match the hint | ` +
          `expectedLen=${expected.length} actualLen=${actual.length}`
      );
      // console.log(
      //   `[CharStyles] Line ${bracketed.target.line.index} | expected/actual | ` +
      //     `${JSON.stringify(expected.slice(0, 120))} … ${JSON.stringify(actual.slice(0, 120))}`
      // );
    }

    planProgress.done();

    const pass1Start = appliedCount;
    let pass1BatchIndex = 0;
    let pass1BatchOps = 0;
    const pass1BatchItems: PreparedTarget[] = [];
    let pass1Processed = 0;

    // Both passes count paragraphs rather than operations: `prepared.length` is
    // known here, while the operation count only emerges as the loop runs.
    const pass1Progress = new StepProgress(
      "Applying character styles (step 1 of 2)",
      prepared.length,
      onProgress
    );

    const flushPass1Batch = async () => {
      if (pass1BatchItems.length === 0) return;
      pass1BatchIndex += 1;
      const operations = pass1BatchOps;
      const items = pass1BatchItems.splice(0);
      pass1BatchOps = 0;
      const cumulative = appliedCount - pass1Start;
      try {
        await sync(`charStyle:pass1Batch${String(pass1BatchIndex).padStart(2, "0")}`, () => ({
          operations,
          paragraphs: items.length,
          cumulativeOperations: cumulative,
        }));
      } catch (batchError) {
        appliedCount -= operations;
        skippedCount += items.length;
        console.warn(
          `[CharStyles] pass-1 batch ${pass1BatchIndex} failed — ${operations} operation(s) across ${items.length} paragraph(s) lost:`,
          batchError
        );
        for (const detail of describeFailedBatch(items, 1)) {
          console.warn(`[CharStyles] pass-1 batch ${pass1BatchIndex} | ${detail}`);
        }
      }
      pass1Progress.set(pass1Processed);
    };

    // Splitting mid-paragraph is safe here in a way it is not in pass 2: every
    // pass-1 action targets the paragraph's own range, so no two paragraphs can
    // produce overlapping or nested inserts. Batches still close on paragraph
    // boundaries to keep the two passes' numbers comparable.
    for (const p of prepared) {
      const before = appliedCount;
      appliedCount += applyRemovalActions(p.line.index, p.range, p.actions);
      appliedCount += applyParagraphStyleActions(p.line.index, p.range, p.actions);
      pass1BatchOps += appliedCount - before;
      pass1BatchItems.push(p);
      pass1Processed += 1;
      if (pass1BatchOps >= PASS1_BATCH_OPERATIONS) {
        await flushPass1Batch();
      }
    }
    await flushPass1Batch();
    pass1Progress.done();
    perf?.note("charStyle:pass1Total", {
      operations: appliedCount - pass1Start,
      batches: pass1BatchIndex,
      trackedRanges: trackedCount,
    });

    // Pass 2 is chunked so its cost is attributable. As one sync it was 75% of
    // the entire run and reported a single opaque number; per-batch timings show
    // whether cost per insert is constant or degrades as the pass proceeds,
    // which synthetic benchmarks could not reproduce.
    //
    // Batches close on paragraph boundaries: a paragraph's entity controls are
    // built with expandTo over its span ranges and can coincide with its own
    // span controls, so splitting one paragraph across two syncs would turn an
    // in-batch overlap into a nested insert against a committed control.
    const pass2Start = appliedCount;
    let batchIndex = 0;
    let batchInserts = 0;
    const pass2BatchItems: PreparedTarget[] = [];
    let pass2Processed = 0;

    const pass2Progress = new StepProgress(
      "Applying character styles (step 2 of 2)",
      prepared.length,
      onProgress
    );

    const flushPass2Batch = async () => {
      if (pass2BatchItems.length === 0) return;
      batchIndex += 1;
      const inserts = batchInserts;
      const items = pass2BatchItems.splice(0);
      batchInserts = 0;
      const cumulative = appliedCount - pass2Start;
      try {
        await sync(`charStyle:pass2Batch${String(batchIndex).padStart(2, "0")}`, () => ({
          ccInserts: inserts,
          paragraphs: items.length,
          cumulativeInserts: cumulative,
        }));
      } catch (batchError) {
        appliedCount -= inserts;
        skippedCount += items.length;
        console.warn(
          `[CharStyles] pass-2 batch ${batchIndex} failed — ${inserts} insert(s) across ${items.length} paragraph(s) lost:`,
          batchError
        );
        for (const detail of describeFailedBatch(items, 2)) {
          console.warn(`[CharStyles] pass-2 batch ${batchIndex} | ${detail}`);
        }
      }
      pass2Progress.set(pass2Processed);
    };

    for (const p of prepared) {
      const before = appliedCount;
      appliedCount += applySpanRemovalActions(p.line.index, p.actions, p.spanRanges);
      appliedCount += applySpanStyleActions(p.line.index, p.actions, p.spanRanges, p.formatting);
      appliedCount += applyEntityStyleActions(p.line.index, p.actions, p.entityRanges);
      batchInserts += appliedCount - before;
      pass2BatchItems.push(p);
      pass2Processed += 1;
      if (batchInserts >= PASS2_BATCH_INSERTS) {
        await flushPass2Batch();
      }
    }
    await flushPass2Batch();
    pass2Progress.done();
    perf?.note("charStyle:pass2Total", {
      ccInserts: appliedCount - pass2Start,
      batches: batchIndex,
    });

    const untrackSpanRanges = () => {
      for (const p of prepared) {
        for (const r of p.spanRanges) {
          if (!r) continue;
          context.trackedObjects.remove(r);
        }
        p.entityRanges.forEach((r) => {
          context.trackedObjects.remove(r);
        });
      }
    };
    await (perf
      ? perf.time("charStyle:untrack", untrackSpanRanges, () => ({ ranges: trackedCount }))
      : untrackSpanRanges());

    // Remaining cost is Word.run discarding its object store — one Range proxy
    // per word fetched. Marked so it stops hiding in stage self time.
    perf?.note("charStyle:beforeTeardown", {
      wordRangeProxies: loaded.reduce((sum, t) => sum + (t.wordRanges?.items.length ?? 0), 0),
    });

    return { applied: appliedCount, skipped: skippedCount, actionRecords, characterStyles };
  });
}